import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { base_url, getToken } from "../../config/config";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FolderIcon from "@mui/icons-material/Folder";
import LabelIcon from "@mui/icons-material/Label";

const API_BASE = `${base_url}/v1`;

const COLOR_PALETTE = [
  "#1976d2",
  "#388e3c",
  "#f57c00",
  "#d32f2f",
  "#7b1fa2",
  "#0097a7",
  "#5d4037",
  "#455a64",
  "#e64a19",
  "#c2185b",
  "#303f9f",
  "#689f38",
  "#fbc02d",
  "#f44336",
  "#9c27b0",
  "#00acc1",
  "#795548",
  "#607d8b",
  "#ff5722",
  "#e91e63",
];

const CategoryManagement = () => {
  const theme = useTheme();

  const [labels, setLabels] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [expandedLabels, setExpandedLabels] = useState(new Set());

  // Dialog states
  const [labelDialog, setLabelDialog] = useState(false);
  const [editingLabel, setEditingLabel] = useState(null);
  const [labelName, setLabelName] = useState("");

  const [subDialog, setSubDialog] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [subFormData, setSubFormData] = useState({
    name: "",
    labelId: "",
    colorCode: "#1976d2",
  });

  useEffect(() => {
    fetchAll();
  }, []);

  const getAuthHeaders = () => {
    const token = getToken();
    return {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchLabels(), fetchSubcategories()]);
    setLoading(false);
  };

  const fetchLabels = async () => {
    try {
      const response = await fetch(`${API_BASE}/getAllLabels`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setLabels(Array.isArray(data) ? data : data.data || []);
    } catch (err) {
      setError("Fehler beim Laden der Kategorien");
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/getAllSubCategoriesEnhanced`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json();
      setSubcategories(data.data || []);
    } catch (err) {
      // Fallback to basic endpoint
      try {
        const response = await fetch(`${API_BASE}/getAllSubCategories`, {
          headers: getAuthHeaders(),
        });
        const data = await response.json();
        setSubcategories(data.data || []);
      } catch (err2) {
        console.error("Error fetching subcategories:", err2);
      }
    }
  };

  // Group subcategories by label
  const getSubcategoriesForLabel = (labelId) => {
    return subcategories.filter(
      (sub) => sub.labelId?._id === labelId || sub.labelId === labelId,
    );
  };

  // Subcategories with no label (standalone)
  const orphanSubcategories = subcategories.filter((sub) => !sub.labelId);

  const toggleExpand = (labelId) => {
    setExpandedLabels((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) {
        next.delete(labelId);
      } else {
        next.add(labelId);
      }
      return next;
    });
  };

  // ============ LABEL (Main Category) CRUD ============

  const handleOpenLabelDialog = (label = null) => {
    if (label) {
      setEditingLabel(label);
      setLabelName(label.name);
    } else {
      setEditingLabel(null);
      setLabelName("");
    }
    setLabelDialog(true);
  };

  const handleCloseLabelDialog = () => {
    setLabelDialog(false);
    setEditingLabel(null);
    setLabelName("");
  };

  const handleSaveLabel = async () => {
    try {
      if (editingLabel) {
        const response = await fetch(`${API_BASE}/updateLabel`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({ id: editingLabel._id, name: labelName }),
        });
        if (!response.ok) throw new Error("Failed to update");
      } else {
        const response = await fetch(`${API_BASE}/createLabel`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({ name: labelName }),
        });
        if (!response.ok) throw new Error("Failed to create");
      }
      setSuccess(
        editingLabel ? "Kategorie aktualisiert" : "Kategorie erstellt",
      );
      handleCloseLabelDialog();
      fetchLabels();
    } catch (err) {
      setError("Fehler beim Speichern der Kategorie");
    }
  };

  const handleDeleteLabel = async (labelId) => {
    const subs = getSubcategoriesForLabel(labelId);
    const msg =
      subs.length > 0
        ? `Diese Kategorie hat ${subs.length} Unterkategorie(n). Trotzdem löschen?`
        : "Möchten Sie diese Kategorie wirklich löschen?";
    if (!window.confirm(msg)) return;

    try {
      const response = await fetch(`${API_BASE}/deleteLabel`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: labelId }),
      });
      if (!response.ok) throw new Error("Failed to delete");
      setSuccess("Kategorie gelöscht");
      fetchAll();
    } catch (err) {
      setError("Fehler beim Löschen der Kategorie");
    }
  };

  // ============ SUBCATEGORY CRUD ============

  const handleOpenSubDialog = (labelId = "", subcategory = null) => {
    if (subcategory) {
      setEditingSubcategory(subcategory);
      setSubFormData({
        name: subcategory.name,
        labelId: subcategory.labelId?._id || subcategory.labelId || "",
        colorCode: subcategory.colorCode || "#1976d2",
      });
    } else {
      setEditingSubcategory(null);
      setSubFormData({
        name: "",
        labelId: labelId,
        colorCode: "#1976d2",
      });
    }
    setSubDialog(true);
  };

  const handleCloseSubDialog = () => {
    setSubDialog(false);
    setEditingSubcategory(null);
    setSubFormData({ name: "", labelId: "", colorCode: "#1976d2" });
  };

  const handleSaveSubcategory = async () => {
    try {
      if (editingSubcategory) {
        const response = await fetch(`${API_BASE}/updateSubCategoryEnhanced`, {
          method: "PUT",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            subcategoryId: editingSubcategory._id,
            name: subFormData.name,
            colorCode: subFormData.colorCode,
          }),
        });
        if (!response.ok) {
          // Fallback to basic update
          const fallback = await fetch(`${API_BASE}/updateSubcategory`, {
            method: "PUT",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              id: editingSubcategory._id,
              name: subFormData.name,
            }),
          });
          if (!fallback.ok) throw new Error("Failed to update");
        }
      } else {
        const body = {
          name: subFormData.name,
          labelId: subFormData.labelId || null,
          colorCode: subFormData.colorCode,
          isPersonal: false,
        };
        const response = await fetch(`${API_BASE}/createSubCategoryEnhanced`, {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          // Fallback to basic create
          const fallback = await fetch(`${API_BASE}/createSubcategory`, {
            method: "POST",
            headers: getAuthHeaders(),
            body: JSON.stringify({
              name: subFormData.name,
              labelId: subFormData.labelId || null,
              isPersonal: false,
            }),
          });
          if (!fallback.ok) throw new Error("Failed to create");
        }
      }
      setSuccess(
        editingSubcategory
          ? "Unterkategorie aktualisiert"
          : "Unterkategorie erstellt",
      );
      handleCloseSubDialog();
      fetchSubcategories();
    } catch (err) {
      setError("Fehler beim Speichern der Unterkategorie");
    }
  };

  const handleDeleteSubcategory = async (subId) => {
    if (!window.confirm("Möchten Sie diese Unterkategorie wirklich löschen?"))
      return;

    try {
      const response = await fetch(`${API_BASE}/deleteSubcategory`, {
        method: "DELETE",
        headers: getAuthHeaders(),
        body: JSON.stringify({ id: subId }),
      });
      if (!response.ok) throw new Error("Failed to delete");
      setSuccess("Unterkategorie gelöscht");
      fetchSubcategories();
    } catch (err) {
      setError("Fehler beim Löschen der Unterkategorie");
    }
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" color={theme.palette.text.primary}>
          Dokument-Kategorien
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenLabelDialog()}
          >
            Hauptkategorie
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenSubDialog()}
          >
            Unterkategorie
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ backgroundColor: theme.palette.background.paper }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Name</TableCell>
                <TableCell>Typ</TableCell>
                <TableCell>Farbe</TableCell>
                <TableCell>Unterkategorien</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {labels.length === 0 && orphanSubcategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color={theme.palette.text.secondary} py={4}>
                      Keine Kategorien vorhanden. Erstellen Sie die erste
                      Kategorie.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {labels.map((label) => {
                    const subs = getSubcategoriesForLabel(label._id);
                    const isExpanded = expandedLabels.has(label._id);

                    return (
                      <React.Fragment key={label._id}>
                        {/* Main Category Row */}
                        <TableRow
                          sx={{
                            backgroundColor: theme.palette.background.default,
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <TableCell>
                            {subs.length > 0 ? (
                              <IconButton
                                size="small"
                                onClick={() => toggleExpand(label._id)}
                              >
                                {isExpanded ? (
                                  <ExpandMoreIcon />
                                ) : (
                                  <ChevronRightIcon />
                                )}
                              </IconButton>
                            ) : (
                              <FolderIcon
                                sx={{
                                  color: theme.palette.text.secondary,
                                  ml: 1,
                                }}
                                fontSize="small"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight="bold" fontSize="1rem">
                              {label.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Hauptkategorie"
                              size="small"
                              color="primary"
                            />
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>
                            <Chip
                              label={`${subs.length} Unterkategorie${subs.length !== 1 ? "n" : ""}`}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSubDialog(label._id)}
                              title="Unterkategorie hinzufügen"
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleOpenLabelDialog(label)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteLabel(label._id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>

                        {/* Subcategory Rows */}
                        {subs.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, border: 0 }}>
                              <Collapse
                                in={isExpanded}
                                timeout="auto"
                                unmountOnExit
                              >
                                <Table size="small">
                                  <TableBody>
                                    {subs.map((sub) => (
                                      <TableRow
                                        key={sub._id}
                                        sx={{
                                          "&:hover": {
                                            backgroundColor:
                                              theme.palette.action.hover,
                                          },
                                        }}
                                      >
                                        <TableCell width={50} />
                                        <TableCell>
                                          <Box
                                            display="flex"
                                            alignItems="center"
                                            gap={1}
                                            pl={3}
                                          >
                                            <LabelIcon
                                              sx={{
                                                color:
                                                  sub.colorCode || "#1976d2",
                                              }}
                                              fontSize="small"
                                            />
                                            <Typography>{sub.name}</Typography>
                                          </Box>
                                        </TableCell>
                                        <TableCell>
                                          <Chip
                                            label="Unterkategorie"
                                            size="small"
                                            variant="outlined"
                                          />
                                        </TableCell>
                                        <TableCell>
                                          <Box
                                            sx={{
                                              width: 24,
                                              height: 24,
                                              borderRadius: 1,
                                              backgroundColor:
                                                sub.colorCode || "#1976d2",
                                              border: `1px solid ${
                                                theme.palette.mode === "dark"
                                                  ? "rgba(255,255,255,0.2)"
                                                  : "rgba(0,0,0,0.1)"
                                              }`,
                                            }}
                                          />
                                        </TableCell>
                                        <TableCell>-</TableCell>
                                        <TableCell align="right">
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleOpenSubDialog("", sub)
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              handleDeleteSubcategory(sub._id)
                                            }
                                            color="error"
                                          >
                                            <DeleteIcon fontSize="small" />
                                          </IconButton>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}

                  {/* Orphan subcategories (no parent label) */}
                  {orphanSubcategories.length > 0 && (
                    <>
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography
                            variant="body2"
                            color={theme.palette.text.secondary}
                            fontStyle="italic"
                            py={1}
                          >
                            Ohne Hauptkategorie
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {orphanSubcategories.map((sub) => (
                        <TableRow
                          key={sub._id}
                          sx={{
                            "&:hover": {
                              backgroundColor: theme.palette.action.hover,
                            },
                          }}
                        >
                          <TableCell />
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1}>
                              <LabelIcon
                                sx={{ color: sub.colorCode || "#1976d2" }}
                                fontSize="small"
                              />
                              <Typography>{sub.name}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label="Unterkategorie"
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: 1,
                                backgroundColor: sub.colorCode || "#1976d2",
                                border: `1px solid ${
                                  theme.palette.mode === "dark"
                                    ? "rgba(255,255,255,0.2)"
                                    : "rgba(0,0,0,0.1)"
                                }`,
                              }}
                            />
                          </TableCell>
                          <TableCell>-</TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenSubDialog("", sub)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSubcategory(sub._id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ============ MAIN CATEGORY (LABEL) DIALOG ============ */}
      <Dialog
        open={labelDialog}
        onClose={handleCloseLabelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingLabel ? "Hauptkategorie bearbeiten" : "Neue Hauptkategorie"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Kategoriename"
              value={labelName}
              onChange={(e) => setLabelName(e.target.value)}
              fullWidth
              required
              placeholder="z.B. Lieferscheine, Rechnungen, Zertifikate..."
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLabelDialog}>Abbrechen</Button>
          <Button
            onClick={handleSaveLabel}
            variant="contained"
            color="secondary"
            disabled={!labelName.trim()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============ SUBCATEGORY DIALOG ============ */}
      <Dialog
        open={subDialog}
        onClose={handleCloseSubDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingSubcategory
            ? "Unterkategorie bearbeiten"
            : "Neue Unterkategorie"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Name"
              value={subFormData.name}
              onChange={(e) =>
                setSubFormData({ ...subFormData, name: e.target.value })
              }
              fullWidth
              required
              placeholder="z.B. Eingangslieferschein, Ausgangsrechnung..."
              autoFocus
            />

            <FormControl fullWidth>
              <InputLabel>Hauptkategorie (optional)</InputLabel>
              <Select
                value={subFormData.labelId}
                label="Hauptkategorie (optional)"
                onChange={(e) =>
                  setSubFormData({ ...subFormData, labelId: e.target.value })
                }
              >
                <MenuItem value="">
                  <em>Keine (eigenständig)</em>
                </MenuItem>
                {labels.map((label) => (
                  <MenuItem key={label._id} value={label._id}>
                    {label.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography
                variant="body2"
                color={theme.palette.text.secondary}
                mb={1}
              >
                Farbe
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {COLOR_PALETTE.map((color) => (
                  <Box
                    key={color}
                    onClick={() =>
                      setSubFormData({ ...subFormData, colorCode: color })
                    }
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: 1,
                      backgroundColor: color,
                      cursor: "pointer",
                      border:
                        subFormData.colorCode === color
                          ? `3px solid ${theme.palette.background.paper}`
                          : "3px solid transparent",
                      boxShadow:
                        subFormData.colorCode === color
                          ? `0 0 0 2px ${theme.palette.text.primary}`
                          : "none",
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSubDialog}>Abbrechen</Button>
          <Button
            onClick={handleSaveSubcategory}
            variant="contained"
            color="secondary"
            disabled={!subFormData.name.trim()}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoryManagement;
